// The availability calendar — one month of what you'll actually be offering.
//
// The days it paints come from POST /availability/preview, which runs the same
// computeSlots() the booking page runs. So this isn't an illustration of the
// schedule that agrees with it today and drifts by Christmas — it IS the
// schedule, asked a different question. A pattern being built is passed along
// unsaved and comes back merged, which is why the calendar fills in as you tap
// day pills rather than after you commit.
//
// A month at a time, because twelve weeks of cells is a wall rather than a
// calendar. The pattern's totals still cover its whole range — the endpoint
// computes those separately — so paging through October doesn't change what the
// count line says.
//
// Tapping a day opens it in place, under its own week, full width. It used to
// toggle the day off, and it used to support a press-and-drag for a range —
// both of which worked, and neither of which was what you actually wanted from
// a day: to see what's in it. Everything that CHANGES a day moved behind the
// pencil in that panel, time off included, so a day is managed in one place
// whether you're adding hours or taking them away.
//
// A day that's off says why. "Off" is the least useful thing a calendar cell
// can tell a barn — whether Kate is at a show or at the dentist changes what
// anybody does about it — so the reason is what the badge carries, and a day
// with hours taken out of the middle keeps its count and gains a note.

import { Fragment } from 'react';

const DAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

// Overlay colors, spelled out because Tailwind scans source for literal class
// names. The primary trainer never uses these — they would compete with the
// badge — so the list starts at the first trainer laid over the top.
export const OVERLAY_DOTS = [
  'bg-violet-500',
  'bg-orange-500',
  'bg-teal-500',
  'bg-pink-500',
  'bg-lime-600',
  'bg-cyan-600',
];

/** All dates from the Sunday on or before `from` to the Saturday on or after `to`. */
function weeksBetween(from, to) {
  const start = new Date(from + 'T00:00:00Z');
  start.setUTCDate(start.getUTCDate() - start.getUTCDay());
  const end = new Date(to + 'T00:00:00Z');
  end.setUTCDate(end.getUTCDate() + (6 - end.getUTCDay()));

  const weeks = [];
  let week = [];
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    week.push(d.toISOString().slice(0, 10));
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
    if (weeks.length > 8) break; // a month spans six weeks at most
  }
  if (week.length) weeks.push(week);
  return weeks;
}

export default function AvailabilityCalendar({
  from,
  to,
  today,
  days = [],
  daysOff = [],
  trainers = [],
  primaryTrainerId,
  onOpenDay,
  openDate = null,
  renderDayDetail,
  onPrevMonth,
  onNextMonth,
  onToday,
  busyDate = null,
  disabled = false,
}) {
  if (!from || !to) return null;

  const byDate = new Map(days.map((d) => [d.date, d]));
  const off = new Set(daysOff);
  const weeks = weeksBetween(from, to);

  // Overlay trainers in the order the endpoint returned them, so a color keeps
  // meaning the same person while you page through months.
  const overlay = trainers.filter((t) => t.userId !== primaryTrainerId);
  const dotFor = (userId) => {
    const i = overlay.findIndex((t) => t.userId === userId);
    return i === -1 ? null : OVERLAY_DOTS[i % OVERLAY_DOTS.length];
  };
  // First name only. "Stephen Cassar 4" doesn't fit a calendar cell, and inside
  // one barn a first name is how people refer to each other anyway.
  const nameFor = (userId) => {
    const t = trainers.find((x) => x.userId === userId);
    return (t?.name || '').trim().split(/\s+/)[0] || 'Trainer';
  };

  const monthLabel = `${MONTHS[Number(from.slice(5, 7)) - 1]} ${from.slice(0, 4)}`;
  const showingToday = today >= from && today <= to;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={onPrevMonth}
          aria-label="Previous month"
          className="w-9 h-9 rounded-md border border-ink-200 text-ink-600 hover:bg-ink-50 leading-none text-lg"
        >
          ‹
        </button>
        <div className="flex items-center gap-2">
          <span className="font-medium">{monthLabel}</span>
          {!showingToday && (
            <button
              type="button"
              onClick={onToday}
              className="text-xs text-ink-500 hover:text-ink-800 underline underline-offset-2"
            >
              Today
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={onNextMonth}
          aria-label="Next month"
          className="w-9 h-9 rounded-md border border-ink-200 text-ink-600 hover:bg-ink-50 leading-none text-lg"
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAY_INITIALS.map((d, i) => (
          <div key={i} className="text-center text-xs font-medium text-ink-400 py-1">
            {d}
          </div>
        ))}
      </div>

      <div className="space-y-1">
        {weeks.map((week, wi) => (
          <Fragment key={wi}>
            <div className="grid grid-cols-7 gap-1">
              {week.map((date) => (
                <DayCell
                  key={date}
                  date={date}
                  today={today}
                  inRange={date >= from && date <= to}
                  day={byDate.get(date)}
                  isOff={off.has(date)}
                  selected={date === openDate}
                  busy={busyDate === date}
                  disabled={disabled}
                  primaryTrainerId={primaryTrainerId}
                  dotFor={dotFor}
                  nameFor={nameFor}
                  hasOverlay={overlay.length > 0}
                  onOpen={onOpenDay}
                />
              ))}
            </div>

            {/* The open day drops in under its own week rather than over the
                top of everything. A day only means something next to the days
                around it — "is Thursday busier than Wednesday" can't be
                answered by a dialog covering Wednesday. */}
            {openDate && week.includes(openDate) && (
              <>
                {/* A caret under the open day, pointing at the panel it
                    expands into. The cell and the panel already share a green;
                    this says which of the seven cells it came from.

                    The offset is computed rather than a flat 1/7, because the
                    grid has a 4px gap between columns — six of them — so a
                    seventh of the width drifts by half a cell by Saturday. */}
                <div className="relative h-2" aria-hidden="true">
                  <span
                    className={
                      'absolute top-0 w-0 h-0 border-x-8 border-x-transparent ' +
                      'border-t-8 border-t-hunter'
                    }
                    style={{
                      left:
                        `calc(${week.indexOf(openDate)} * ((100% - 24px) / 7 + 4px)` +
                        ` + ((100% - 24px) / 7) / 2)`,
                      transform: 'translateX(-50%)',
                    }}
                  />
                </div>
                {renderDayDetail?.(openDate)}
              </>
            )}
          </Fragment>
        ))}
      </div>

      {days.some((d) => d.fromProposed) && (
        <p className="inline-flex items-center gap-1.5 mt-3 text-xs text-ink-400">
          <span className="w-3 h-3 rounded border-2 border-dashed border-ink-400" />
          Dashed days come from the pattern below, and aren&apos;t saved yet.
        </p>
      )}
    </div>
  );
}

function DayCell({
  date, today, inRange, day, isOff, selected, busy, disabled,
  primaryTrainerId, dotFor, nameFor, hasOverlay, onOpen,
}) {
  const dayNum = Number(date.slice(8, 10));
  const isToday = date === today;
  const slots = day?.slots ?? 0;
  const booked = day?.booked ?? 0;
  const overridden = day?.overridden ?? false;
  // Why the day is off, and hours taken out of a day that otherwise runs.
  // "off" tells a barn nothing it wanted to know; "Show" tells it everything.
  const offLabel = day?.offLabel ?? null;
  const partialOff = day?.partialOff ?? null;
  // Some of this day's slots come from the pattern being built and aren't saved
  // yet. Without saying so the calendar reads as fact while the day panel — which
  // only knows what's stored — reads as a contradiction.
  const proposed = day?.fromProposed ?? false;
  const available = slots > 0 && !isOff;

  // Days from the neighbouring months still render — a whole-weeks grid needs
  // the whole week — but sit grayed and inert, the way every calendar does it.
  if (!inRange) {
    return (
      <div className="aspect-square rounded-md bg-ink-50/50 p-1.5">
        <span className="text-sm font-semibold text-ink-200">{dayNum}</span>
      </div>
    );
  }

  // Two things share each cell: the date, and what that date does. Tinting the
  // whole cell says which category a day is in; the badge carries the number,
  // so the count never reads as a second date.
  let tone = 'bg-white border-ink-100';
  let badge = '';
  if (isOff) {
    tone = 'bg-ink-100 border-ink-200';
    badge = 'bg-ink-400 text-white';
  } else if (overridden) {
    tone = 'bg-sky-50 border-sky-300';
    badge = 'bg-sky-600 text-white';
  } else if (booked > 0) {
    tone = 'bg-amber-50 border-amber-300';
    badge = 'bg-amber-600 text-white';
  } else if (available) {
    tone = 'bg-hunter/10 border-hunter/40';
    badge = 'bg-hunter text-white';
  }

  // Other trainers available this day, as a row of dots. Deliberately not counts:
  // the question an overlay answers is "is anybody covering Thursday", and six
  // numbers in a 45px cell answers nothing.
  const allOthers = Object.entries(day?.byTrainer ?? {})
    .filter(([id]) => id !== primaryTrainerId)
    .map(([id, v]) => ({
      id,
      dot: dotFor(id),
      slots: v.slots,
      booked: v.booked,
      off: v.off,
      offLabel: v.offLabel ?? null,
    }))
    .filter((t) => t.dot);
  // Three named rows plus the primary's badge is what fits a square cell. Past
  // that the cell would silently clip, so it says how many it isn't showing.
  const MAX_ROWS = 3;
  const others = allOthers.slice(0, MAX_ROWS);
  const hiddenCount = allOthers.length - others.length;

  return (
    <button
      type="button"
      data-date={date}
      disabled={disabled}
      onClick={() => onOpen?.(date)}
      aria-expanded={selected}
      aria-label={
        `${date}` +
        (isOff
          ? ` — off${offLabel ? `, ${offLabel}` : ''}`
          : available
            ? ` — ${slots} slots`
            : '') +
        (partialOff && !isOff ? `, away ${partialOff.startTime}–${partialOff.endTime}` : '')
      }
      className={
        'aspect-square rounded-md border p-1.5 flex flex-col items-start text-left ' +
        'transition-colors relative select-none overflow-hidden ' +
        tone +
        (disabled ? ' opacity-60' : ' hover:brightness-95 cursor-pointer') +
        (proposed ? ' border-dashed border-2 ' : '') +
        (isToday ? ' ring-2 ring-ink-800 ring-offset-1' : '') +
        (selected ? ' ring-2 ring-hunter ring-offset-1 brightness-95' : '') +
        (busy ? ' opacity-50' : '')
      }
    >
      <span
        className={
          'text-sm font-semibold leading-none ' + (isToday ? 'text-ink-900' : 'text-ink-700')
        }
      >
        {dayNum}
      </span>

      {/* One bottom-anchored stack whatever it holds, so rows line up across a
          week rather than floating at whatever height their contents reach.

          With nobody overlaid the badge can speak in words — this is one
          person's calendar and the tint already says whose. The moment somebody
          else is on it, every trainer gets the same solid badge in their own
          color and the same booked/total. A filled badge for one person and a
          dot with gray text for another read as a hierarchy that isn't there,
          and which trainer happens to be selected up top shouldn't change how
          the day is written: you expand it to see the detail either way. */}
      <span className="mt-auto flex flex-col items-start gap-0.5 max-w-full w-full">
        {!hasOverlay
          ? (isOff || slots > 0) && (
              <span
                className={
                  'px-1.5 py-0.5 rounded text-[10px] font-medium leading-none ' +
                  'whitespace-nowrap max-w-full overflow-hidden ' +
                  badge
                }
              >
                {isOff ? (
                  offLabel ? (
                    <>
                      {/* A phone cell is about 37px of usable width, where
                          "Appointment" arrives as "App…" and costs the reader
                          more than it tells them. The word it replaced is the
                          better answer at that size; the reason is one tap
                          away in the panel. A typed reason can be any length,
                          so even with room the cell clips rather than grows. */}
                      <span className="sm:hidden">off</span>
                      <span className="hidden sm:block truncate">{offLabel}</span>
                    </>
                  ) : (
                    <>
                      <span className="hidden sm:inline">day </span>off
                    </>
                  )
                ) : booked > 0 ? (
                  <>
                    <span className="tabular-nums">{booked}/{slots}</span>
                    <span className="hidden sm:inline"> booked</span>
                  </>
                ) : (
                  <>
                    <span className="tabular-nums">{slots}</span>
                    <span className="hidden sm:inline"> {slots === 1 ? 'slot' : 'slots'}</span>
                  </>
                )}
              </span>
            )
          : [
              // The focused trainer leads, then the rest in chip order, so the
              // stack reads the same way as the row above the calendar.
              ...(isOff || slots > 0
                ? [{ id: primaryTrainerId, tone: badge, off: isOff, offLabel, booked, slots }]
                : []),
              ...others.map((t) => ({
                id: t.id,
                tone: t.dot + ' text-white',
                off: t.off,
                offLabel: t.offLabel,
                booked: t.booked,
                slots: t.slots,
              })),
            ].map((t) => (
              <span
                key={t.id}
                className={
                  'px-1.5 py-0.5 rounded text-[10px] font-medium leading-none ' +
                  'whitespace-nowrap max-w-full overflow-hidden ' +
                  t.tone
                }
              >
                {/* The name only where there's room. A phone cell is about 37px
                    of usable width, and the count is the half that can't go. */}
                <span className="hidden md:inline">{nameFor(t.id)} </span>
                {t.off ? (
                  <>
                    <span className="md:hidden">off</span>
                    <span className="hidden md:inline">{t.offLabel || 'off'}</span>
                  </>
                ) : (
                  <span className="tabular-nums">{t.booked}/{t.slots}</span>
                )}
              </span>
            ))}

        {/* Hours out of a day that otherwise runs. It gets its own muted
            badge under the count rather than replacing it, because the count
            is still true — the morning is bookable and the badge says so. */}
        {partialOff && !isOff && (
          <span
            className={
              'px-1.5 py-0.5 rounded text-[10px] font-medium leading-none ' +
              'max-w-full overflow-hidden bg-ink-200 text-ink-700'
            }
          >
            <span className="sm:hidden">away</span>
            <span className="hidden sm:block truncate">{partialOff.label || 'away'}</span>
          </span>
        )}

        {hiddenCount > 0 && (
          <span className="text-[10px] text-ink-400 leading-none">+{hiddenCount} more</span>
        )}
      </span>
    </button>
  );
}
